<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'WComment',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parentId: {
      type: String,
      required: false,
    },
    parent: {
      type: Object,
      required: true,
    },
    container: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const resource = ref(null);
  const editor = ref(null);
  const createTitle = ref($fn.value.pgettext('Comments', 'Add comment'));
  const editTitle = ref($fn.value.pgettext('Comments', 'Edit comment'));
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'md',
  }));

  const $data = computed(() => ({
    resource: resource.value,
    editor: editor.value,
    createTitle: createTitle.value,
    editTitle: editTitle.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_resource = () => {
    return (
      props.item ||
      $fn.value.schemaDefaults('CreateComment', {
        object_id: props.parentId || props.parent.id,
      })
    );
  };
  const $$load_editor = () => {
    return resource.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'resource' },
      { name: 'editor', dependsOn: ['resource'] },
      { name: 'createTitle' },
      { name: 'editTitle' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { resource: $$load_resource, editor: $$load_editor },
    ux: {},
    dataRefs: {
      resource,
      editor,
      createTitle,
      editTitle,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title:
          (props.item == null) == null ? createTitle.value : editTitle.value,
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.parentId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parentId');
    }
  );
  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );
  watch(
    () => props.container,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('container');
    }
  );
  watch(
    () => props.item,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('item');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_w2h6gcktx_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_1040167445267876_closeComponent_5s20k76vx_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        value: $event.data.response.data,
      },
    };
  };
  const act_1040167445267876_closeComponent_1se0iwqsb_getActionArgs = async (
    $event
  ) => {
    return {
      result: {},
    };
  };
  const act_1040167445267876_rest_3h7pah3qb_getActionArgs = async ($event) => {
    return {
      method: 'save',
      container: props.container,
      resource: editor.value,
    };
  };

  const act_7315092382398562_closeModal_w2h6gcktx =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_w2h6gcktx_getActionArgs,
    });
  const inst_evh_7315092382398562_close_237ztb9zj =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_w2h6gcktx,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_237ztb9zj],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_k2lvpk5f1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_6hey55vhf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_k2lvpk5f1,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_6hey55vhf],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_levk13orn =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_073f3i3zh =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_levk13orn,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_073f3i3zh],
    event: 'close',
  });
  const act_1040167445267876_closeComponent_5s20k76vx =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_5s20k76vx_getActionArgs,
    });
  const inst_evh_1040167445267876_saved_0ggvu7puh =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_5s20k76vx,
      event: 'saved',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_closeComponent_1se0iwqsb =
    new actionTypes.CloseComponentAction({
      actionArgs: act_1040167445267876_closeComponent_1se0iwqsb_getActionArgs,
    });
  const inst_evh_1040167445267876_deleted_4uw7ut4t6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeComponent_1se0iwqsb,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_1040167445267876_rest_3h7pah3qb = new actionTypes.RestAction({
    actionArgs: act_1040167445267876_rest_3h7pah3qb_getActionArgs,
    displayName: 'submit',
    events: [
      inst_evh_1040167445267876_saved_0ggvu7puh,
      inst_evh_1040167445267876_deleted_4uw7ut4t6,
    ],
  });
  const inst_evh_1040167445267876_submit_t62hdsf0c =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_rest_3h7pah3qb,
      event: 'submit',
      displayName: 'submit',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_t62hdsf0c],
    event: 'submit',
  });
  const act_2434514588044777_closeComponent_iemkr0s5 =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_cafwf9yo7 =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_iemkr0s5,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_cafwf9yo7],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_237ztb9zj,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_6hey55vhf,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_073f3i3zh,
    inst_evh_8564662037462133_close,
    inst_evh_1040167445267876_saved_0ggvu7puh,
    inst_evh_1040167445267876_deleted_4uw7ut4t6,
    inst_evh_1040167445267876_submit_t62hdsf0c,
    submit: inst_evh_1040167445267876_submit_t62hdsf0c,
    inst_evh_1040167445267876_submit,
    inst_evh_2434514588044777_clickPreventStop_cafwf9yo7,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_237ztb9zj = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_237ztb9zj.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_6hey55vhf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_6hey55vhf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_073f3i3zh = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_073f3i3zh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_saved_0ggvu7puh = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_saved_0ggvu7puh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_deleted_4uw7ut4t6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_deleted_4uw7ut4t6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_t62hdsf0c = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_t62hdsf0c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_cafwf9yo7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_cafwf9yo7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="WComment"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :title="documentTitle"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        name="comment"
        v-if="loaded"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpFormGroup name="message">
          <ItpFormTextarea
            name="message"
            v-model="editor.message"
            required
            autofocus
            :rows="8"
            :placeholder="gettext('Comment message')"
          >
          </ItpFormTextarea>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          :form="forms.comment"
          :text="gettext('Save')"
          type="submit"
          :spinning="actions.submit.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          :disabled="actions.submit.isRunning"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
